import { useCallback } from 'preact/hooks';

import { usePress } from '../hooks/UNSAFE_usePress';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useNavigationListContext } from './useNavigationListContext';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import {
  NavigationListItemStyles,
  NavigationListItemVariantOptions
} from '../UNSAFE_NavigationList/themes/NavigationListItemStyles.css';
import { NavigationListItemRedwoodTheme } from './themes/redwood/NavigationListItemTheme';

const flexItemStyleInterpolations = mergeInterpolations<FlexitemProps>([
  ...Object.values(flexitemInterpolations)
]);
const { class: labelContainerFlexItemClasses, ...labelContainerFlexItemStyle } =
  flexItemStyleInterpolations({
    alignSelf: 'center'
  });

export function useNavigationListItem<K extends string | number>({ itemKey }: { itemKey: K }) {
  const { onSelectionChange, selection, currentKey, showFocusRing, navigationListItemPrefix } =
    useNavigationListContext();

  //Selection and focus state
  const isCurrent = currentKey === itemKey;

  const isSelected = selection === itemKey;

  //Styling
  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle, applyActiveStyle } =
    useInteractionStyle();

  const {
    classes: listItemClasses,
    styles: { labelContainerClasses }
  } = useComponentTheme<NavigationListItemVariantOptions, NavigationListItemStyles>(
    NavigationListItemRedwoodTheme,
    {
      focusRing: showFocusRing ? 'isFocusRing' : 'notFocusRing',
      current: isCurrent ? 'isCurrent' : 'notCurrtent',
      selected: isSelected ? 'isSelected' : 'notSelected',
      hybridHover: applyHoverStyle ? 'isHybridHover' : 'notHybridHover',
      pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      active: applyActiveStyle ? 'isActive' : 'notActive'
    }
  );

  //Item handlers
  const handlePress = useCallback(() => {
    onSelectionChange?.({ value: itemKey as K, reason: 'pointer' });
  }, [itemKey, onSelectionChange]);

  //Legacy navlist makes selection on keyDown, but since preact tabbar made selection after keyUp, will do the same
  //for preact navlist
  const { pressProps } = usePress(handlePress);

  return {
    itemId: navigationListItemPrefix + itemKey,
    itemHandlers: mergeProps(interactionProps, pressProps /*,onFocusProps*/),
    itemClasses: classNames([listItemClasses]),
    isSelected,
    labelContainerClasses: classNames([labelContainerClasses, labelContainerFlexItemClasses]),
    labelContainerStyle: labelContainerFlexItemStyle
  };
}
